import React, { useState, useEffect, useRef, useCallback } from 'react';
import { clientNoteApi, getMediaUrl, getAuthSession } from '../services/api';
import useSocket from '../hooks/useSocket';
import WorksheetFormModal from './WorksheetFormModal';

export default function LiveCollaborativeSheet({
  noteId,
  initialNote,
  currentUserRole = 'client', // 'client' or 'physiotherapist' or 'admin'
  onClose,
  readOnly = false,
}) {
  const authSession = getAuthSession();
  const currentUserName = authSession?.user?.name || (currentUserRole === 'physiotherapist' ? 'Therapist' : 'Client');
  const currentUserId = authSession?.user?._id || authSession?.user?.id;

  const [noteData, setNoteData] = useState(initialNote || null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [sheetData, setSheetData] = useState(() => {
    const raw = initialNote?.worksheet?.sheetData;
    if (raw && Array.isArray(raw.columns) && raw.columns.length > 0) {
      return {
        title: raw.title || initialNote?.worksheet?.title || 'Interactive Worksheet',
        columns: raw.columns,
        rows: Array.isArray(raw.rows) && raw.rows.length > 0 ? raw.rows : [Array(raw.columns.length).fill('')],
      };
    }
    return {
      title: initialNote?.worksheet?.title || 'Clinical Reflection Sheet',
      columns: ['Date / Step', 'Situation / Trigger', 'Thought / Feeling (1-10)', 'Response / Action Plan', 'Therapist Remarks'],
      rows: [['Day 1', '', '', '', '']],
    };
  });

  const [clientResponses, setClientResponses] = useState(initialNote?.worksheet?.clientResponses || {});
  const [syncStatus, setSyncStatus] = useState('synced'); // 'synced' | 'saving' | 'live_update' | 'error'
  const [lastEditedInfo, setLastEditedInfo] = useState(initialNote?.worksheet?.lastEditedBy || null);
  const [activeCollaborators, setActiveCollaborators] = useState([]);
  const [activeCell, setActiveCell] = useState({ row: null, col: null });
  const [typingCollaborator, setTypingCollaborator] = useState(null);
  const [embedIframeError, setEmbedIframeError] = useState(false);

  const saveTimerRef = useRef(null);
  const typingTimerRef = useRef(null);

  const {
    socket,
    joinWorksheet,
    leaveWorksheet,
    sendWorksheetCellChange,
    sendWorksheetSync,
    onWorksheetCellUpdate,
    onWorksheetDataSynced,
    onWorksheetUserJoined,
    onWorksheetUserLeft,
  } = useSocket();

  // Load fresh note data if needed
  useEffect(() => {
    if (noteId && !initialNote) {
      clientNoteApi.getWorksheetNoteById(noteId)
        .then((res) => {
          if (res?.note) {
            setNoteData(res.note);
            if (res.note.worksheet?.sheetData?.columns?.length > 0) {
              setSheetData(res.note.worksheet.sheetData);
            }
            if (res.note.worksheet?.clientResponses) {
              setClientResponses(res.note.worksheet.clientResponses);
            }
            if (res.note.worksheet?.lastEditedBy) {
              setLastEditedInfo(res.note.worksheet.lastEditedBy);
            }
          }
        })
        .catch((err) => console.error('Failed to load worksheet:', err));
    }
  }, [noteId, initialNote]);

  // Real-time socket room subscription
  useEffect(() => {
    if (!noteId) return;

    joinWorksheet(noteId, currentUserName, currentUserRole);

    onWorksheetUserJoined((data) => {
      if (data.worksheetId === noteId && data.userId !== currentUserId) {
        setActiveCollaborators((prev) => {
          const filtered = prev.filter((u) => u.userId !== data.userId);
          return [...filtered, { userId: data.userId, name: data.userName, role: data.userRole }];
        });
      }
    });

    onWorksheetUserLeft((data) => {
      if (data.worksheetId === noteId) {
        setActiveCollaborators((prev) => prev.filter((u) => u.userId !== data.userId));
      }
    });

    onWorksheetCellUpdate((data) => {
      if (data.worksheetId === noteId && data.userId !== currentUserId) {
        setSheetData((prev) => {
          const newRows = prev.rows.map((r, rIdx) => {
            if (rIdx !== data.rowIndex) return [...r];
            const updatedRow = [...r];
            updatedRow[data.colIndex] = data.value;
            return updatedRow;
          });
          return { ...prev, rows: newRows };
        });

        setTypingCollaborator(`${data.userName || (data.userRole === 'physiotherapist' ? 'Therapist' : 'Client')} is typing...`);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingCollaborator(null), 2500);

        setSyncStatus('live_update');
        setTimeout(() => setSyncStatus('synced'), 1200);
      }
    });

    onWorksheetDataSynced((data) => {
      if (data.worksheetId === noteId && data.userId !== currentUserId) {
        if (data.sheetData) {
          setSheetData(data.sheetData);
        }
        if (data.clientResponses) {
          setClientResponses(data.clientResponses);
        }
        if (data.lastEditedBy) {
          setLastEditedInfo(data.lastEditedBy);
        }
        setSyncStatus('live_update');
        setTimeout(() => setSyncStatus('synced'), 1200);
      }
    });

    return () => {
      leaveWorksheet(noteId, currentUserName);
    };
  }, [
    noteId,
    currentUserName,
    currentUserRole,
    currentUserId,
    joinWorksheet,
    leaveWorksheet,
    onWorksheetUserJoined,
    onWorksheetUserLeft,
    onWorksheetCellUpdate,
    onWorksheetDataSynced,
  ]);

  // Persist to backend API (debounced)
  const saveSheetToBackend = useCallback(
    async (updatedSheet, updatedResponses) => {
      if (!noteId || readOnly) return;
      setSyncStatus('saving');
      try {
        await clientNoteApi.syncLiveWorksheet(noteId, {
          sheetData: updatedSheet,
          clientResponses: updatedResponses || clientResponses,
        });
        setSyncStatus('synced');
        setLastEditedInfo({
          userId: currentUserId,
          name: currentUserName,
          role: currentUserRole,
        });
      } catch (err) {
        console.error('Error auto-saving worksheet:', err);
        setSyncStatus('error');
      }
    },
    [noteId, readOnly, clientResponses, currentUserId, currentUserName, currentUserRole]
  );

  // Handle cell edit
  const handleCellChange = (rowIndex, colIndex, value) => {
    if (readOnly) return;

    const newRows = sheetData.rows.map((row, rIdx) => {
      if (rIdx !== rowIndex) return row;
      const copy = [...row];
      copy[colIndex] = value;
      return copy;
    });

    const updatedSheet = { ...sheetData, rows: newRows };
    setSheetData(updatedSheet);

    // Broadcast instant cell update via socket
    sendWorksheetCellChange(noteId, rowIndex, colIndex, value, currentUserName, currentUserRole);

    // Debounce save to database
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveSheetToBackend(updatedSheet, clientResponses);
      sendWorksheetSync(noteId, updatedSheet, clientResponses, currentUserName, currentUserRole);
    }, 800);
  };

  // Add row
  const handleAddRow = () => {
    if (readOnly) return;
    const emptyRow = Array(sheetData.columns.length).fill('');
    const updatedSheet = { ...sheetData, rows: [...sheetData.rows, emptyRow] };
    setSheetData(updatedSheet);
    saveSheetToBackend(updatedSheet, clientResponses);
    sendWorksheetSync(noteId, updatedSheet, clientResponses, currentUserName, currentUserRole);
  };

  // Delete row
  const handleDeleteRow = (indexToDelete) => {
    if (readOnly || sheetData.rows.length <= 1) return;
    const newRows = sheetData.rows.filter((_, idx) => idx !== indexToDelete);
    const updatedSheet = { ...sheetData, rows: newRows };
    setSheetData(updatedSheet);
    saveSheetToBackend(updatedSheet, clientResponses);
    sendWorksheetSync(noteId, updatedSheet, clientResponses, currentUserName, currentUserRole);
  };

  // Add column
  const handleAddColumn = () => {
    if (readOnly) return;
    const colName = window.prompt('Enter new column header name:', `Column ${sheetData.columns.length + 1}`);
    if (!colName || !colName.trim()) return;

    const newCols = [...sheetData.columns, colName.trim()];
    const newRows = sheetData.rows.map((row) => [...row, '']);
    const updatedSheet = { ...sheetData, columns: newCols, rows: newRows };
    setSheetData(updatedSheet);
    saveSheetToBackend(updatedSheet, clientResponses);
    sendWorksheetSync(noteId, updatedSheet, clientResponses, currentUserName, currentUserRole);
  };

  // Export as CSV
  const handleExportCSV = () => {
    const csvRows = [
      sheetData.columns.map((c) => `"${(c || '').replace(/"/g, '""')}"`).join(','),
      ...sheetData.rows.map((row) =>
        row.map((cell) => `"${(cell || '').replace(/"/g, '""')}"`).join(',')
      ),
    ];
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `${(sheetData.title || 'worksheet').replace(/\s+/g, '_')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const ws = noteData?.worksheet || initialNote?.worksheet;
  const isGoogleSheet = ws?.linkUrl && (ws.linkUrl.includes('docs.google.com/spreadsheets') || ws.linkUrl.includes('google.com'));

  return (
    <div
      className="collaborative-sheet-container"
      style={{
        background: '#ffffff',
        borderRadius: '16px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top Header Bar */}
      <div
        style={{
          padding: '16px 22px',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
          color: '#ffffff',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div
            style={{
              width: '42px',
              height: '42px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '22px',
              boxShadow: '0 4px 10px rgba(16,185,129,0.3)',
            }}
          >
            📊
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#f8fafc' }}>
              {sheetData.title || ws?.title || 'Interactive Clinical Worksheet'}
            </h3>
            <p style={{ margin: '2px 0 0 0', fontSize: '12.5px', color: '#94a3b8' }}>
              {noteData?.clientName ? `Client: ${noteData.clientName}` : ''} • Real-Time Collaborative Grid (Live Sync)
            </p>
          </div>
        </div>

        {/* Sync status & Collaborator Indicators */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          {typingCollaborator && (
            <span
              style={{
                fontSize: '12px',
                color: '#38bdf8',
                background: 'rgba(56,189,248,0.15)',
                padding: '4px 10px',
                borderRadius: '20px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                animation: 'pulse 1.5s infinite',
              }}
            >
              ✍️ {typingCollaborator}
            </span>
          )}

          {/* Sync Status Badge */}
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '5px 12px',
              borderRadius: '20px',
              fontSize: '12px',
              fontWeight: 600,
              background:
                syncStatus === 'synced'
                  ? 'rgba(16,185,129,0.2)'
                  : syncStatus === 'saving'
                  ? 'rgba(245,158,11,0.2)'
                  : syncStatus === 'live_update'
                  ? 'rgba(59,130,246,0.2)'
                  : 'rgba(239,68,68,0.2)',
              color:
                syncStatus === 'synced'
                  ? '#34d399'
                  : syncStatus === 'saving'
                  ? '#fbbf24'
                  : syncStatus === 'live_update'
                  ? '#60a5fa'
                  : '#f87171',
              border: '1px solid currentColor',
            }}
          >
            <span
              style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                background: 'currentColor',
                display: 'inline-block',
              }}
            ></span>
            {syncStatus === 'synced'
              ? '🟢 Live Synced'
              : syncStatus === 'saving'
              ? '🟡 Saving...'
              : syncStatus === 'live_update'
              ? '🔵 Updating...'
              : '🔴 Sync Error'}
          </div>

          {onClose && (
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                color: '#ffffff',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                cursor: 'pointer',
                fontSize: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="Close Sheet"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Dynamic Form Bar if worksheet has interactive questionnaire */}
      {(() => {
        const hasForm = Boolean(
          ws?.hasDynamicForm ||
          (Array.isArray(ws?.dynamicFields) && ws.dynamicFields.length > 0) ||
          ws?.worksheetId?.hasDynamicForm ||
          (Array.isArray(ws?.worksheetId?.dynamicFields) && ws.worksheetId.dynamicFields.length > 0)
        );
        if (!hasForm) return null;
        const isSubmitted = Boolean(ws?.isFormSubmitted || noteData?.worksheet?.isFormSubmitted);

        return (
          <div
            style={{
              padding: '10px 20px',
              background: '#f5f3ff',
              borderBottom: '1px solid #ddd6fe',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '10px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#5b21b6' }}>
              <span style={{ fontSize: '16px' }}>📝</span>
              <span>
                <strong>Interactive Form Available:</strong>{' '}
                {isSubmitted
                  ? 'Client has completed and submitted this form.'
                  : 'Interactive questionnaire pending client response.'}
              </span>
            </div>
            <button
              type="button"
              onClick={() => setShowFormModal(true)}
              style={{
                padding: '6px 14px',
                background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '12.5px',
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: '0 2px 6px rgba(124,58,237,0.3)',
              }}
            >
              📋 {currentUserRole === 'physiotherapist' ? 'View Filled Form & Feedback' : 'Open & Fill Form'}
            </button>
          </div>
        );
      })()}

      {/* Reference document bar (if admin worksheet file is attached) */}
      {ws?.fileUrl && (
        <div
          style={{
            padding: '10px 20px',
            background: '#f8fafc',
            borderBottom: '1px solid #e2e8f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '10px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '16px' }}>📑</span>
            <div style={{ fontSize: '12.5px', color: '#475569' }}>
              Reference Document: <strong>{ws.fileName || ws.title || 'Worksheet Document'}</strong>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <a
              href={getMediaUrl(ws.fileUrl)}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                padding: '5px 12px',
                background: '#0284c7',
                color: '#ffffff',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '12px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              👁️ View PDF
            </a>
          </div>
        </div>
      )}

      {/* Spreadsheet Action Toolbar */}
      <div
        style={{
          padding: '12px 20px',
          background: '#f8fafc',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '10px',
        }}
      >
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {!readOnly && (
            <>
              <button
                type="button"
                onClick={handleAddRow}
                style={{
                  background: '#0284c7',
                  color: '#ffffff',
                  border: 'none',
                  padding: '7px 14px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                ➕ Add Row
              </button>
              <button
                type="button"
                onClick={handleAddColumn}
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  border: '1px solid #cbd5e1',
                  padding: '7px 14px',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                }}
              >
                ➕ Add Column
              </button>
            </>
          )}
          <button
            type="button"
            onClick={handleExportCSV}
            style={{
              background: '#f1f5f9',
              color: '#334155',
              border: '1px solid #cbd5e1',
              padding: '7px 14px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '5px',
            }}
          >
            📥 Export CSV
          </button>
        </div>

        {lastEditedInfo && (
          <div style={{ fontSize: '12px', color: '#64748b' }}>
            Last edited by <strong style={{ color: '#0f172a' }}>{lastEditedInfo.name || 'User'}</strong> ({lastEditedInfo.role || 'Member'})
          </div>
        )}
      </div>

      {/* Spreadsheet Grid Table */}
      <div style={{ overflowX: 'auto', maxHeight: '550px', position: 'relative' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '13.5px',
            background: '#ffffff',
            textAlign: 'left',
          }}
        >
          <thead>
            <tr style={{ background: '#f1f5f9', borderBottom: '2px solid #cbd5e1' }}>
              <th style={{ width: '45px', padding: '12px 10px', textAlign: 'center', color: '#64748b', fontSize: '12px', borderRight: '1px solid #e2e8f0' }}>
                #
              </th>
              {sheetData.columns.map((colName, colIdx) => (
                <th
                  key={colIdx}
                  style={{
                    padding: '12px 14px',
                    fontWeight: 700,
                    color: '#1e293b',
                    borderRight: '1px solid #e2e8f0',
                    minWidth: '180px',
                    background: colIdx % 2 === 0 ? '#f8fafc' : '#f1f5f9',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '6px' }}>
                    <span>{colName}</span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', background: '#ffffff', padding: '1px 5px', borderRadius: '4px', border: '1px solid #cbd5e1' }}>
                      {String.fromCharCode(65 + (colIdx % 26))}
                    </span>
                  </div>
                </th>
              ))}
              {!readOnly && (
                <th style={{ width: '50px', padding: '12px 8px', textAlign: 'center', color: '#64748b' }}>
                  Action
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sheetData.rows.map((row, rowIdx) => (
              <tr
                key={rowIdx}
                style={{
                  borderBottom: '1px solid #e2e8f0',
                  background: rowIdx % 2 === 0 ? '#ffffff' : '#fafafa',
                  transition: 'background 0.15s',
                }}
              >
                {/* Row Number */}
                <td
                  style={{
                    padding: '10px 8px',
                    textAlign: 'center',
                    fontWeight: 600,
                    color: '#64748b',
                    fontSize: '12px',
                    borderRight: '1px solid #e2e8f0',
                    background: '#f8fafc',
                    userSelect: 'none',
                  }}
                >
                  {rowIdx + 1}
                </td>

                {/* Editable Cells */}
                {sheetData.columns.map((_, colIdx) => {
                  const cellValue = row[colIdx] || '';
                  const isFocused = activeCell.row === rowIdx && activeCell.col === colIdx;

                  return (
                    <td
                      key={colIdx}
                      style={{
                        padding: '4px',
                        borderRight: '1px solid #e2e8f0',
                        position: 'relative',
                        background: isFocused ? '#eff6ff' : 'transparent',
                      }}
                    >
                      <textarea
                        value={cellValue}
                        onChange={(e) => handleCellChange(rowIdx, colIdx, e.target.value)}
                        onFocus={() => setActiveCell({ row: rowIdx, col: colIdx })}
                        onBlur={() => setActiveCell({ row: null, col: null })}
                        readOnly={readOnly}
                        placeholder={readOnly ? '' : 'Type text / notes here...'}
                        rows={Math.max(1, (cellValue.match(/\n/g) || []).length + 1)}
                        style={{
                          width: '100%',
                          minHeight: '38px',
                          padding: '8px 10px',
                          border: isFocused ? '2px solid #2563eb' : '1px solid transparent',
                          borderRadius: '4px',
                          fontSize: '13.5px',
                          color: '#0f172a',
                          background: 'transparent',
                          outline: 'none',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          lineHeight: '1.4',
                          boxSizing: 'border-box',
                        }}
                      />
                    </td>
                  );
                })}

                {/* Row Delete Action */}
                {!readOnly && (
                  <td style={{ textAlign: 'center', padding: '6px' }}>
                    <button
                      type="button"
                      onClick={() => handleDeleteRow(rowIdx)}
                      disabled={sheetData.rows.length <= 1}
                      title={sheetData.rows.length <= 1 ? 'Cannot delete only remaining row' : 'Delete Row'}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        color: sheetData.rows.length <= 1 ? '#cbd5e1' : '#ef4444',
                        cursor: sheetData.rows.length <= 1 ? 'not-allowed' : 'pointer',
                        fontSize: '14px',
                        padding: '4px',
                        borderRadius: '4px',
                      }}
                    >
                      🗑️
                    </button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer Info & Instructions */}
      <div
        style={{
          padding: '12px 20px',
          background: '#f8fafc',
          borderTop: '1px solid #e2e8f0',
          fontSize: '12px',
          color: '#64748b',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '8px',
        }}
      >
        <div>
          💡 <strong>Live Collaboration:</strong> Any edits made by client or therapist synchronize in real time.
        </div>
        <div>
          {sheetData.rows.length} row{sheetData.rows.length > 1 ? 's' : ''} • {sheetData.columns.length} column{sheetData.columns.length > 1 ? 's' : ''}
        </div>
      </div>

      {/* Interactive Form Modal */}
      {showFormModal && (
        <WorksheetFormModal
          note={noteData || initialNote}
          isTherapist={currentUserRole === 'physiotherapist'}
          onClose={() => setShowFormModal(false)}
          onUpdated={(updated) => {
            setNoteData(updated);
            if (updated?.worksheet?.clientResponses) {
              setClientResponses(updated.worksheet.clientResponses);
            }
          }}
        />
      )}
    </div>
  );
}
